import React from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import { ClinicalTrialsOutcomesModel } from '../../../../../models/data-sections/clinical-trials/outcomesModel';

interface StudyOutcomesProps {
  outcomes: ClinicalTrialsOutcomesModel[];
}

const StudyOutcomes: React.FC<StudyOutcomesProps> = ({ outcomes }) => {
  if (!outcomes || outcomes.length === 0) {
    return (
      <Typography sx={{ textAlign: 'center', mt: 4 }}>
        No outcomes data available.
      </Typography>
    );
  }

  // Define columns
  const columns: GridColDef[] = [
    {
      field: 'outcome_type',
      headerName: 'Outcome Type',
      width: 200,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value || 'N/A'}
        </div>
      ),
    },
    {
      field: 'title',
      headerName: 'Title',
      width: 400,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value || 'N/A'}
        </div>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value || 'N/A'}
        </div>
      ),
    },
  ];

  // Map rows for DataGrid
  const rows = outcomes.map((outcome, index) => ({
    id: index, // Unique ID for each row
    outcome_type: outcome.outcome_type,
    title: outcome.title,
    description: outcome.description,
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%', // Ensure it fits the parent container
        width: '100%',
      }}
    >
      {rows.length > 0 ? (
        <DataGrid
          rows={rows}
          columns={columns}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'} // Dynamically adjust row height based on content
          sx={{
            flex: 1, // Ensure the DataGrid fills the container space
            fontFamily: 'Segoe UI',
            '& .MuiDataGrid-columnHeader.wrap-header': {
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              textAlign: 'left', // Align headers to the left
              lineHeight: '1.2',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 'normal',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: '10px 12px',
              textAlign: 'left', // Align column headers left
              height: 'unset !important',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f0f0f0',
              fontWeight: 'bold',
              fontSize: '.8rem',
              maxHeight: '168px !important',
            },
            '& .MuiDataGrid-row': {
              backgroundColor: '#ffffff',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#e0f7fa',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: '#ffffff',
              borderTop: '1px solid #e0e0e0',
            },
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal', // Allow text wrapping in cells
              wordWrap: 'break-word', // Enable wrapping
              lineHeight: '1.5', // Adjust line height for better readability
              overflow: 'visible', // Prevent truncation
              textAlign: 'left', // Align text to the left
              verticalAlign: 'top', // Align content to the top of the cell
              padding: '8px', // Add padding to the cells
            },
          }}
        />
      ) : (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          No outcomes data available.
        </div>
      )}
    </Box>
  );
};

export default StudyOutcomes;
